import { Info } from 'lucide-react';
import { useState } from 'react';
import { matchLevel, type MatchLevel } from '../audio/match';

const LABEL: Record<MatchLevel, string> = {
  'very-close': 'Very close',
  close: 'Close',
  partly: 'Partly there',
  different: 'Quite different',
};

const TIP: Record<MatchLevel, string> = {
  'very-close': 'Rhythm and sounds follow the model well.',
  close: 'Most of it matches. Play both again and listen for the differences.',
  partly: 'Listen again and copy the rhythm and the stressed syllables.',
  different: 'Listen once more, slowly if it helps, then try again.',
};

interface Props {
  /** 0–100, null for "no speech heard", undefined while working it out. */
  score: number | null | undefined;
}

/**
 * The rough match between the recording and the model, as a percentage with a word for it.
 * It describes rhythm and sounds only, and says so: the learner's ears and grade still decide.
 */
export function MatchMeter({ score }: Props) {
  const [why, setWhy] = useState(false);
  if (score === undefined) {
    return (
      <p className="small muted" aria-live="polite">
        Comparing with the model…
      </p>
    );
  }
  if (score === null) {
    return (
      <p className="small" role="status">
        We couldn't hear speech in your recording. Try again a little louder, or closer to the microphone.
      </p>
    );
  }
  const level = matchLevel(score);
  return (
    <div className="match" data-level={level}>
      <div className="row" style={{ gap: 10 }}>
        <span className="match-score" aria-hidden="true">
          {score}%
        </span>
        <div className="stack" style={{ gap: 2, flex: 1 }}>
          <span className="match-label">{LABEL[level]} to the model</span>
          <div
            className="match-bar"
            role="meter"
            aria-label="Match with the model"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={score}
            aria-valuetext={`${score} percent, ${LABEL[level].toLowerCase()}`}
          >
            <span style={{ width: `${score}%` }} />
          </div>
        </div>
        <button type="button" className="icon-btn icon-btn-plain" aria-expanded={why} aria-label="What does the match mean?" onClick={() => setWhy((w) => !w)}>
          <Info size={18} aria-hidden="true" />
        </button>
      </div>
      <p className="small muted">{TIP[level]}</p>
      {why && (
        <p className="small muted reveal">
          A rough comparison of the rhythm and sounds of your recording with the model, worked out on this device. It can't
          tell whether every word or the stress was right, so trust your ears and grade yourself.
        </p>
      )}
    </div>
  );
}
